import { useState } from 'react';
import { Play } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import {
  ImpactRecord,
  getYoutubeId,
  useImpactRecords,
} from '@/hooks/useImpactRecords';
import TestimonialVideoModal from './TestimonialVideoModal';

export default function TestimonialsCarousel() {
  const { data: records = [], isLoading } = useImpactRecords({ category: 'testimonio' });
  const [active, setActive] = useState<ImpactRecord | null>(null);

  // Only those with valid YouTube videos
  const items = records.filter((r) => getYoutubeId(r.video_url));

  if (isLoading || items.length === 0) return null;

  return (
    <section>
      <div className="text-center mb-10">
        <Badge variant="outline" className="mb-4">Testimonios</Badge>
        <h2 className="text-2xl md:text-3xl font-bold mb-4">
          Qué Dicen Nuestros Usuarios
        </h2>
      </div>

      <Carousel
        opts={{ align: 'start', loop: items.length > 3 }}
        className="px-12"
      >
        <CarouselContent>
          {items.map((t) => {
            const ytId = getYoutubeId(t.video_url)!;
            const thumb =
              t.image_url ||
              `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
            return (
              <CarouselItem
                key={t.id}
                className="md:basis-1/2 lg:basis-1/3"
              >
                <Card
                  className="overflow-hidden cursor-pointer group hover:shadow-lg transition-shadow h-full"
                  onClick={() => setActive(t)}
                >
                  <div className="relative aspect-video bg-muted overflow-hidden">
                    <img
                      src={thumb}
                      alt={t.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div className="h-14 w-14 rounded-full bg-primary/90 text-primary-foreground flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="h-6 w-6 ml-0.5 fill-current" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-semibold text-sm">{t.title}</div>
                    {t.subtitle && (
                      <div className="text-xs text-muted-foreground">
                        {t.subtitle}
                      </div>
                    )}
                  </div>
                </Card>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <CarouselPrevious />
        <CarouselNext />
      </Carousel>

      <TestimonialVideoModal testimonial={active} onClose={() => setActive(null)} />
    </section>
  );
}
